import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const titleMap = {
  men: "For Him",
  women: "For Her",
  unisex: "Unisex",
};

const ShopPage = () => {
  const { gender } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const brandFilter = searchParams.get("brand") || "all";

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/brands");
      setBrands(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (gender) params.gender = gender;
      if (brandFilter && brandFilter !== "all") params.brand = brandFilter;
      const { data } = await api.get("/products", { params });
      setProducts(data);
      setLoading(false);
    })();
  }, [gender, brandFilter]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, sort]);

  const title = gender ? titleMap[gender] || "Shop" : "All Perfumes";
  const subtitle = gender
    ? `Compositions crafted ${gender === "unisex" ? "for everyone" : `for ${gender === "men" ? "him" : "her"}`}.`
    : "Our complete edit — every bottle in stock.";

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-12 md:pt-16 pb-24">
      <header className="border-b border-gold-soft pb-10 mb-12">
        <div className="eyebrow mb-4">{gender ? "Collection" : "The Edit"}</div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-ink tracking-tight leading-[0.95]">
          {title}
        </h1>
        <p className="mt-5 text-graphite max-w-xl">{subtitle}</p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-10">
        <div className="flex items-center gap-3">
          <span className="eyebrow text-ash">Brand</span>
          <Select
            value={brandFilter}
            onValueChange={(v) => {
              const next = new URLSearchParams(searchParams);
              if (v === "all") next.delete("brand");
              else next.set("brand", v);
              setSearchParams(next);
            }}
          >
            <SelectTrigger
              data-testid="filter-brand-select"
              className="w-[180px] h-10 border-gold-soft bg-transparent text-xs uppercase tracking-widest rounded-none"
            >
              <SelectValue placeholder="All brands" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {brands.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-3">
          <span className="eyebrow text-ash">Sort</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              data-testid="filter-sort-select"
              className="w-[180px] h-10 border-gold-soft bg-transparent text-xs uppercase tracking-widest rounded-none"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-asc">Price: Low → High</SelectItem>
              <SelectItem value="price-desc">Price: High → Low</SelectItem>
              <SelectItem value="name">Name (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto text-xs text-ash">
          {sorted.length} {sorted.length === 1 ? "product" : "products"}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-sand animate-pulse" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div data-testid="no-products" className="py-32 text-center">
          <div className="eyebrow text-ash mb-3">Nothing found</div>
          <p className="font-serif text-3xl text-ink">Try a different filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
          {sorted.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopPage;
